import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Quagga from "quagga";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import {
  ArrowLeft, Package, CheckCircle2, Truck, QrCode,
  Trash2, FileText, Tag, Plus, RefreshCw, ScanLine,
  ChevronDown, ChevronUp, X, BoxIcon, Download,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import { BASE_URL } from "../../const";

// â”€â”€ auth â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function authHeader() {
  const t = localStorage.getItem("token");
  if (!t) return {};
  return { Authorization: t.startsWith("Bearer ") || t.startsWith("Token ") ? t : `Token ${t}` };
}

// â”€â”€ helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function nextBoxId(existingBoxes) {
  const nums = existingBoxes
    .map(b => b.box_id)
    .filter(Boolean)
    .map(id => parseInt((id.match(/\d+$/) || ["0"])[0], 10));
  const max = nums.length ? Math.max(...nums) : 0;
  return `BOX-${String(max + 1).padStart(4, "0")}`;
}

function classifyBoxes(boxes) {
  const pending   = boxes.filter(b => !b.tracking_status || b.tracking_status === "pending");
  const confirmed = boxes.filter(b => b.tracking_status === "booked");
  const shipped   = boxes.filter(b => ["in_transit", "out_for_delivery", "delivered"].includes(b.tracking_status));
  return { pending, confirmed, shipped };
}

function sanitizeIntegerInput(value) {
  return value.replace(/\D/g, "");
}

function groupBoxesByBoxId(rows) {
  const map = {};
  for (const row of rows) {
    const key = String(row.box_id || row.id);
    const skuKey = `${row.sku || ""}__${row.sku_name || ""}`;
    if (!map[key]) {
      map[key] = {
        ...row,
        rowIds: [row.id],
        id: row.id,
        qty: Number(row.qty) || 0,
        skus: [row.sku].filter(Boolean),
        skuNames: [row.sku_name].filter(Boolean),
        skuLineMap: {
          [skuKey]: {
            sku: row.sku || "",
            sku_name: row.sku_name || "",
            qty: Number(row.qty) || 0,
          },
        },
      };
      continue;
    }
    const box = map[key];
    box.rowIds.push(row.id);
    box.qty = (Number(box.qty) || 0) + (Number(row.qty) || 0);
    if (row.sku) box.skus.push(row.sku);
    if (row.sku_name) box.skuNames.push(row.sku_name);
    if (!box.skuLineMap[skuKey]) {
      box.skuLineMap[skuKey] = {
        sku: row.sku || "",
        sku_name: row.sku_name || "",
        qty: 0,
      };
    }
    box.skuLineMap[skuKey].qty += Number(row.qty) || 0;
  }
  return Object.values(map).map(box => ({
    ...box,
    skus: Array.from(new Set(box.skus)),
    skuNames: Array.from(new Set(box.skuNames)),
    skuLines: Object.values(box.skuLineMap || {}),
  }));
}

// â”€â”€ toast â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function Toast({ toasts, remove }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <div
          key={t.id}
          className={cn(
            "pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium min-w-[260px] animate-in slide-in-from-bottom-2",
            t.type === "error"   && "bg-red-50 border-red-200 text-red-800",
            t.type === "success" && "bg-green-50 border-green-200 text-green-800",
            t.type === "info"    && "bg-blue-50 border-blue-200 text-blue-800",
          )}
        >
          <span className="flex-1">{t.msg}</span>
          <button onClick={() => remove(t.id)} className="opacity-50 hover:opacity-100"><X size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function useToast() {
  const [toasts, setToasts] = useState([]);
  function toast(msg, type = "info") {
    const id = Date.now();
    setToasts(p => [...p, { id, msg, type }]);
    setTimeout(() => setToasts(p => p.filter(t => t.id !== id)), 3500);
  }
  function remove(id) { setToasts(p => p.filter(t => t.id !== id)); }
  return { toasts, toast, remove };
}

// â”€â”€ StatusBadge â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function StatusBadge({ status }) {
  const map = {
    pending:          { label: "Pending",       cls: "bg-orange-100 text-orange-700 border-orange-200" },
    booked:           { label: "Scan Pending",  cls: "bg-amber-100  text-amber-700  border-amber-200"  },
    in_transit:       { label: "Shipped",       cls: "bg-green-100  text-green-700  border-green-200"  },
    out_for_delivery: { label: "Out for Del.",  cls: "bg-green-100  text-green-700  border-green-200"  },
    delivered:        { label: "Delivered",     cls: "bg-green-100  text-green-700  border-green-200"  },
  };
  const s = map[status] || { label: status || "Unknown", cls: "bg-gray-100 text-gray-600 border-gray-200" };
  return (
    <span className={cn("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border", s.cls)}>
      {s.label}
    </span>
  );
}

// â”€â”€ Section Header â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function SectionHeader({ icon: Icon, title, count, color, collapsed, onToggle }) {
  return (
    <div
      className={cn("flex items-center justify-between px-4 py-3 rounded-xl border font-semibold text-sm cursor-pointer select-none", color)}
      onClick={onToggle}
    >
      <div className="flex items-center gap-2">
        <Icon size={15} />
        {title}
        <span className="ml-1 px-2 py-0.5 rounded-full bg-white/60 text-xs font-bold">{count}</span>
      </div>
      {collapsed ? <ChevronDown size={15} /> : <ChevronUp size={15} />}
    </div>
  );
}

// â”€â”€ Box Card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function BoxCard({ box, reqMap, onConfirm, onScan, onDelete, onAction, confirming, scanning, deleting }) {
  const req = reqMap[box.requirement_id] || {};
  const totalQty = Number(box.qty) || 0;
  const skuLines = box.skuLines?.length
    ? box.skuLines
    : [{ sku: box.sku || req.sku || "â€”", sku_name: box.sku_name || req.sku_name || "â€”", qty: totalQty }];

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* card header */}
      <div className="flex items-start justify-between px-4 pt-3 pb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-bold text-sm text-gray-900 font-mono">{box.box_id || "â€”"}</span>
            <StatusBadge status={box.tracking_status || "pending"} />
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {box.weight ? `${box.weight} kg ` : ""}{totalQty} unit{totalQty !== 1 ? "s" : ""}
          </div>
        </div>
        {onDelete && (
          <button
            onClick={() => onDelete(box)}
            disabled={deleting}
            className="text-gray-300 hover:text-red-400 transition-colors p-1"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* SKU lines */}
      <div className="mx-4 mb-3 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
        <div className="flex flex-col gap-2">
          {skuLines.map((line, idx) => (
            <div key={`${line.sku}-${line.sku_name}-${idx}`} className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="font-mono text-xs font-bold text-violet-700 truncate block">{line.sku || "â€”"}</span>
                <span className="text-xs text-gray-500 truncate block">{line.sku_name || "â€”"}</span>
              </div>
              <span className="text-xs font-bold text-gray-700 shrink-0">Qty- {Number(line.qty) || 0}</span>
            </div>
          ))}
        </div>
      </div>

      {/* invoice number */}
      {box.invoice_number && (
        <div className="mx-4 mb-3 text-xs text-gray-400">
          Invoice: <span className="font-mono text-gray-600">{box.invoice_number}</span>
        </div>
      )}

      {/* actions */}
      <div className="border-t border-gray-100 px-4 py-2.5">
        {(box.tracking_status || "pending") === "pending" && (
          <Button
            size="sm"
            className="w-full bg-gray-900 hover:bg-gray-700 text-white h-8"
            onClick={() => onConfirm(box)}
            disabled={confirming === box.id}
          >
            {confirming === box.id ? <RefreshCw size={13} className="animate-spin mr-1" /> : <CheckCircle2 size={13} className="mr-1" />}
            Confirm box
          </Button>
        )}

        {box.tracking_status === "booked" && (
          <div className="flex flex-col gap-1.5">
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onAction("invoice", box)}>
                <FileText size={12} className="mr-1" /> Invoice
              </Button>
              <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onAction("label", box)}>
                <Tag size={12} className="mr-1" /> Label
              </Button>
            </div>
            <Button
              size="sm"
              className="w-full bg-gray-900 hover:bg-gray-700 text-white h-8"
              onClick={() => onScan(box)}
              disabled={scanning === box.id}
            >
              {scanning === box.id ? <RefreshCw size={13} className="animate-spin mr-1" /> : <ScanLine size={13} className="mr-1" />}
              Scan box
            </Button>
          </div>
        )}

        {["in_transit", "out_for_delivery", "delivered"].includes(box.tracking_status) && (
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onAction("invoice", box)}>
              <FileText size={12} className="mr-1" /> Invoice
            </Button>
            <Button size="sm" variant="outline" className="flex-1 h-8 text-xs" onClick={() => onAction("label", box)}>
              <Tag size={12} className="mr-1" /> Label
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// â”€â”€ Camera Scanner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function CameraScanner({ onScanned, onClose, expectedAwb, expectedBoxId }) {
  const videoRef = useRef(null);
  const cooldownRef = useRef(false);
  const processingRef = useRef(false);
  const detectedHandlerRef = useRef(null);
  const scannerActiveRef = useRef(false);
  const lastProcessedAwbRef = useRef("");
  const [camError, setCamError] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [manualAwb, setManualAwb] = useState("");
  const [scannerPaused, setScannerPaused] = useState(false);
  const manualRef = useRef(null);

  function stopScanner() {
    try {
      if (detectedHandlerRef.current) {
        Quagga.offDetected(detectedHandlerRef.current);
        detectedHandlerRef.current = null;
      }
      Quagga.stop();
    } catch (_) {}
    scannerActiveRef.current = false;

    try {
      const vid = videoRef.current;
      if (vid?.srcObject) {
        vid.srcObject.getTracks().forEach(t => t.stop());
        vid.srcObject = null;
      }
    } catch (_) {}
  }

  function startScanner() {
    if (!videoRef.current || scannerActiveRef.current) return;
    setCamError(null);

    Quagga.init({
      numOfWorkers: 1,
      locate: true,
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: videoRef.current,
        constraints: { facingMode: "environment", width: { min: 640 }, height: { min: 480 } },
      },
      frequency: 10,
      decoder: { readers: ["code_128_reader"] },
      debug: false,
    }, (err) => {
      if (err) {
        setCamError("Camera failed to start. Check camera permissions.");
        setTimeout(() => manualRef.current?.focus(), 100);
        return;
      }

      Quagga.start();
      scannerActiveRef.current = true;

      const onDetected = (result) => {
        const code = result?.codeResult?.code?.trim();
        if (!code || code.length < 8) return;
        if (cooldownRef.current || processingRef.current) return;

        cooldownRef.current = true;
        setTimeout(() => { cooldownRef.current = false; }, 1500);
        handleDetect(code);
      };

      detectedHandlerRef.current = onDetected;
      Quagga.onDetected(onDetected);
    });
  }

  useEffect(() => {
    startScanner();
    return () => {
      stopScanner();
    };
  }, []); // eslint-disable-line

  async function handleDetect(awb) {
    const normalizedAwb = String(awb || "").trim();
    if (!normalizedAwb) return;
    if (processingRef.current) return;
    if (lastProcessedAwbRef.current === normalizedAwb) return;

    processingRef.current = true;
    lastProcessedAwbRef.current = normalizedAwb;
    stopScanner();
    setScannerPaused(true);

    const result = await onScanned(normalizedAwb);
    setLastResult({ awb: normalizedAwb, ...result });
    processingRef.current = false;
  }

  async function handleManualSubmit() {
    const awb = manualAwb.trim();
    if (!awb) return;
    await handleDetect(awb);
    setManualAwb("");
  }

  function handleScanNext() {
    processingRef.current = false;
    lastProcessedAwbRef.current = "";
    setLastResult(null);
    setScannerPaused(false);
    startScanner();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
      <div className="relative bg-white rounded-2xl overflow-hidden shadow-2xl w-full max-w-md">

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <QrCode size={16} className="text-violet-600" />
            <span className="font-semibold text-sm text-gray-800">Scan AWB Barcode</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>

        {/* Camera view */}
        <div className="relative bg-black" style={{ height: 260 }}>
          <div ref={videoRef} style={{ width: "100%", height: "100%", overflow: "hidden" }} />

          {/* Aim guide */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-60 h-20">
              <div className="absolute top-0 left-0 w-5 h-5 border-t-2 border-l-2 border-violet-400 rounded-tl-sm" />
              <div className="absolute top-0 right-0 w-5 h-5 border-t-2 border-r-2 border-violet-400 rounded-tr-sm" />
              <div className="absolute bottom-0 left-0 w-5 h-5 border-b-2 border-l-2 border-violet-400 rounded-bl-sm" />
              <div className="absolute bottom-0 right-0 w-5 h-5 border-b-2 border-r-2 border-violet-400 rounded-br-sm" />
              <div className="absolute left-2 right-2 top-1/2 h-px bg-violet-400 opacity-70 animate-pulse" />
            </div>
          </div>

          {camError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 px-6">
              <div className="text-center">
                <p className="text-red-400 text-sm font-medium">{camError}</p>
                <p className="text-gray-400 text-xs mt-1">Use the input below</p>
              </div>
            </div>
          )}
        </div>

        {/* Last scan result */}
        {lastResult && (
          <div className={cn(
            "mx-4 mt-3 px-3 py-2 rounded-lg text-xs font-medium border",
            lastResult.success
              ? "bg-green-50 text-green-700 border-green-200"
              : "bg-red-50 text-red-700 border-red-200"
          )}>
            <span className="font-mono">{lastResult.awb}</span>
            {" - "}{lastResult.message}
          </div>
        )}

        {scannerPaused && (
          <div className="mx-4 mt-3 flex gap-2">
            <Button size="sm" className="h-8 bg-gray-900 hover:bg-gray-700 text-white" onClick={handleScanNext}>
              Scan next
            </Button>
            <Button size="sm" variant="outline" className="h-8" onClick={onClose}>
              Close
            </Button>
          </div>
        )}

        {/* {expectedAwb && (
          <div className="mx-4 mt-3 px-3 py-2 rounded-lg text-xs font-medium border bg-blue-50 text-blue-700 border-blue-200">
            Match required for <span className="font-semibold">{expectedBoxId || "selected box"}</span>:
            {" "}
            <span className="font-mono">{expectedAwb}</span>
          </div>
        )} */}

        {/* Manual / hardware-scanner fallback */}
        <div className="p-4">
          <p className="text-xs text-gray-400 mb-2">Or enter / paste AWB (hardware scanner / manual):</p>
          <div className="flex gap-2">
            <Input
              ref={manualRef}
              value={manualAwb}
              onChange={e => setManualAwb(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleManualSubmit()}
              placeholder="Enter AWB number..."
              className="h-9 text-sm font-mono"
              autoFocus={!!camError}
            />
            <Button
              size="sm"
              className="h-9 bg-gray-900 hover:bg-gray-700 text-white shrink-0"
              onClick={handleManualSubmit}
            >
              <ScanLine size={13} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
// MAIN PAGE
// â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•

export default function InvoiceProcessing() {
  const location = useLocation();
  const navigate = useNavigate();
  const requirementGroup = location.state?.requirementGroup || [];
  const firstReq = requirementGroup[0] || {};
  const reqMap   = Object.fromEntries(requirementGroup.map(r => [String(r.id), r]));

  const { toasts, toast, remove: removeToast } = useToast();

  // â”€â”€ boxes state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [boxes, setBoxes]         = useState([]);
  const [loadingBoxes, setLoadingBoxes] = useState(true);
  const [confirming, setConfirming]     = useState(null);
  const [scanning, setScanning]         = useState(null);
  const [deleting, setDeleting]         = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [refreshing, setRefreshing]       = useState(false);
  const [downloadingLabels, setDownloadingLabels] = useState(false);

  // â”€â”€ vendor status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const COMPLETED_STATUSES = ["fully_dispatched", "cancelled"];
  const initialGroup = COMPLETED_STATUSES.includes(firstReq.vendor_status) ? "completed" : "open";
  const [vendorStatusGroup, setVendorStatusGroup] = useState(initialGroup);
  const [updatingStatus, setUpdatingStatus]       = useState(false);

  // â”€â”€ quick add form â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const defaultReqId = requirementGroup[0]?.id || "";
  const [qaReqId,      setQaReqId]      = useState(defaultReqId);
  const [qaNumBoxes,   setQaNumBoxes]   = useState("1");
  const [qaQtyPerBox,  setQaQtyPerBox]  = useState("1");
  const [qaWeight,     setQaWeight]     = useState("");
  const [qaInvoice,    setQaInvoice]    = useState("");
  const [qaAdding,     setQaAdding]     = useState(false);

  // â”€â”€ manual box builder â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [showManual,   setShowManual]   = useState(false);
  const [manLines,     setManLines]     = useState([{ reqId: defaultReqId, qty: "1" }]);
  const [manWeight,    setManWeight]    = useState("");
  const [manInvoice,   setManInvoice]   = useState("");
  const [manAdding,    setManAdding]    = useState(false);

  // â”€â”€ sections collapse â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [pendingOpen,   setPendingOpen]   = useState(true);
  const [confirmedOpen, setConfirmedOpen] = useState(true);
  const [shippedOpen,   setShippedOpen]   = useState(true);

  // â”€â”€ scanner â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [cameraOpen, setCameraOpen] = useState(false);
  const [scanTargetBox, setScanTargetBox] = useState(null);

  // â”€â”€ redirect if no group â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  useEffect(() => {
    if (!requirementGroup.length) navigate("/purchase/invoicing");
  }, []); // eslint-disable-line

  // â”€â”€ fetch boxes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const fetchBoxes = useCallback(async () => {
    setLoadingBoxes(true);
    try {
      const res  = await fetch(
        `${BASE_URL}/vendor/invoices/?request_code=${encodeURIComponent(firstReq.request_code)}&page_size=500`,
        { headers: authHeader() }
      );
      const data = await res.json();
      const list = data?.results || data?.data?.results || data?.data || [];
      setBoxes(list);
    } catch (e) { toast("Failed to load boxes: " + e.message, "error"); }
    finally { setLoadingBoxes(false); }
  }, [firstReq.request_code]); // eslint-disable-line

  useEffect(() => { fetchBoxes(); }, [fetchBoxes]);

  const groupedBoxes = groupBoxesByBoxId(boxes);

  // â”€â”€ derived â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const { pending, confirmed, shipped } = classifyBoxes(groupedBoxes);

  // dispatched qty per requirement id
  const dispatchedByReqId = {};
  for (const box of boxes) {
    dispatchedByReqId[box.requirement_id] = (dispatchedByReqId[box.requirement_id] || 0) + (Number(box.qty) || 0);
  }

  // â”€â”€ quick add â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleQuickAdd() {
    const numBoxes  = parseInt(qaNumBoxes, 10);
    const qtyPerBox = parseInt(qaQtyPerBox, 10);
    if (!qaReqId)          { toast("Select a SKU / requirement", "error"); return; }
    if (!qaInvoice.trim()) { toast("Enter an invoice number", "error"); return; }
    if (isNaN(numBoxes) || numBoxes < 1)   { toast("Number of boxes must be â‰¥ 1", "error"); return; }
    if (isNaN(qtyPerBox) || qtyPerBox < 1) { toast("Qty per box must be â‰¥ 1", "error"); return; }

    const req = reqMap[String(qaReqId)];
    if (!req) { toast("Invalid SKU selection", "error"); return; }

    setQaAdding(true);
    try {
      // Generate all box IDs client-side upfront, then send as one bulk request
      const temp = [...boxes];
      const payloads = [];
      for (let i = 0; i < numBoxes; i++) {
        const boxId = nextBoxId(temp);
        temp.push({ box_id: boxId });
        payloads.push({
          requirement_id: String(qaReqId),
          invoice_number: qaInvoice.trim(),
          box_id:          boxId,
          sku:             req.sku,
          sku_name:        req.sku_name || "",
          qty:             qtyPerBox,
          weight:          qaWeight ? parseInt(qaWeight, 10) : null,
          tracking_status: "pending",
        });
      }

      const res  = await fetch(`${BASE_URL}/vendor/invoices/create/`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(payloads),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));

      const body = data?.data;
      // 207 = boxes saved but EE failed; body.records holds the DB rows
      const list = Array.isArray(body)
        ? body
        : (body?.records || (body && !body.records ? [body] : []));
      setBoxes(prev => [...prev, ...list]);

      if (res.status === 207 && body?.ee_errors?.length) {
        const detail = body.ee_errors.map(e => `${e.box_id}: ${e.error}`).join(" | ");
        toast(`Boxes saved but EasyEcom failed â€” ${detail}`, "error");
      } else {
        toast(`${numBoxes} box${numBoxes > 1 ? "es" : ""} created`, "success");
      }
      setQaNumBoxes("1"); setQaQtyPerBox("1"); setQaWeight("");
    } catch (e) { toast("Error: " + e.message, "error"); }
    finally { setQaAdding(false); }
  }

  // â”€â”€ manual box â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleManualAdd() {
    const validLines = manLines.filter(l => l.reqId && parseInt(l.qty, 10) > 0);
    if (!validLines.length)   { toast("Add at least one SKU line", "error"); return; }
    if (!manInvoice.trim())   { toast("Enter an invoice number", "error"); return; }

    const boxId = nextBoxId(groupedBoxes);
    const payloads = validLines
      .map(line => {
        const req = reqMap[String(line.reqId)];
        if (!req) return null;
        return {
          requirement_id: String(line.reqId),
          invoice_number: manInvoice.trim(),
          box_id:          boxId,
          sku:             req.sku,
          sku_name:        req.sku_name || "",
          qty:             parseInt(line.qty, 10),
          weight:          manWeight ? parseInt(manWeight, 10) : null,
          tracking_status: "pending",
        };
      })
      .filter(Boolean);

    if (!payloads.length) { toast("No valid SKU lines", "error"); return; }

    setManAdding(true);
    try {
      const res  = await fetch(`${BASE_URL}/vendor/invoices/create/`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify(payloads),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));

      const body = data?.data;
      const list = Array.isArray(body)
        ? body
        : (body?.records || (body && !body.records ? [body] : []));
      setBoxes(prev => [...prev, ...list]);

      if (res.status === 207 && body?.ee_errors?.length) {
        const detail = body.ee_errors.map(e => `${e.box_id}: ${e.error}`).join(" | ");
        toast(`Box saved but EasyEcom failed â€” ${detail}`, "error");
      } else {
        toast(`Manual box ${boxId} created`, "success");
      }
      setManLines([{ reqId: defaultReqId, qty: "1" }]);
      setManWeight(""); setManInvoice(""); setShowManual(false);
    } catch (e) { toast("Error: " + e.message, "error"); }
    finally { setManAdding(false); }
  }

  // â”€â”€ confirm all pending boxes â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleConfirmAll() {
    if (!pending.length) return;
    setConfirmingAll(true);
    try {
      const ids = pending.flatMap(b => b.rowIds || [b.id]);
      const res = await fetch(`${BASE_URL}/vendor/invoices/confirm-bulk/`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Confirm all failed");

      const results      = data?.data?.results || [];
      const confirmedCnt = data?.data?.confirmed || 0;
      const failedCnt    = data?.data?.failed    || 0;

      const confirmedBoxIds = new Set(results.filter(r => r.status).map(r => r.box_id));
      setBoxes(prev => prev.map(b =>
        confirmedBoxIds.has(b.box_id) ? { ...b, tracking_status: "booked" } : b
      ));

      if (failedCnt === 0) {
        toast(`${confirmedCnt} box${confirmedCnt !== 1 ? "es" : ""} confirmed`, "success");
      } else {
        const failedIds = results.filter(r => !r.status).map(r => `${r.box_id}: ${r.message}`).join("; ");
        toast(`${confirmedCnt} confirmed - ${failedCnt} failed â€” ${failedIds}`, "error");
      }
    } catch (e) { toast("Error: " + e.message, "error"); }
    finally { setConfirmingAll(false); }
  }

  // â”€â”€ confirm box â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleConfirm(box) {
    setConfirming(box.id);
    try {
      const res  = await fetch(`${BASE_URL}/vendor/invoices/${box.id}/`, {
        method: "PUT",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "confirm" }),
      });
      const data = await res.json();
      if (!res.ok) {
        // EE confirm failed â€” stay pending, surface the error
        const eeErr = data?.errors?.ee_error || [];
        throw new Error(data?.message || `EE confirm failed: ${eeErr.join(", ") || "unknown error"}`);
      }
      const result    = data?.data || {};
      const record    = result.record || {};
      const eeSuccess = result.ee_success || [];
      setBoxes(prev => prev.map(b =>
        b.id === box.id ? { ...b, ...record, tracking_status: "booked" } : b
      ));
      const eeId = eeSuccess[0] ? ` - EE #${eeSuccess[0]}` : "";
      toast(`${box.box_id} confirmed${eeId}`, "success");
    } catch (e) { toast(e.message, "error"); }
    finally { setConfirming(null); }
  }

  // â”€â”€ scan AWB â€” calls /scan/ endpoint, triggers EE manifest â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleScanAWB(awb) {
    if (!awb) {
      toast("AWB not available â€” refresh from EasyEcom first", "info");
      return { success: false, message: "No AWB" };
    }
    try {
      const res  = await fetch(`${BASE_URL}/vendor/invoices/scan/`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ awb }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data?.message || "Scan failed";
        toast(msg, "error");
        return { success: false, message: msg };
      }
      const record = data?.data?.record;
      if (record) {
        setBoxes(prev => prev.map(b => b.id === record.id ? { ...b, ...record } : b));
      }
      const warning = data?.data?.ee_warning;
      if (warning) {
        toast(`${awb} scanned · ${warning}`, "info");
      } else {
        toast(`${awb} scanned & manifest generated`, "success");
      }
      return { success: true, message: warning ? `Scanned - ${warning}` : "Scanned & manifested" };
    } catch (e) {
      toast("Scan error: " + e.message, "error");
      return { success: false, message: e.message };
    }
  }

  // â”€â”€ scan box card button â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleScan(box) {
    if (!box.ee_awb_no) {
      toast(`${box.box_id}: no AWB assigned â€” refresh from EasyEcom first`, "info");
      return;
    }
    setScanTargetBox(box);
    setCameraOpen(true);
  }

  async function handleCameraScanned(scannedAwb) {
    const scanned = String(scannedAwb || "").trim();
    if (!scanned) {
      return { success: false, message: "No AWB scanned" };
    }

    if (!scanTargetBox) {
      return handleScanAWB(scanned);
    }

    const expected = String(scanTargetBox.ee_awb_no || "").trim();
    const expected_ee_order_id = String(scanTargetBox.ee_order_id || "").trim();

    const normalizedScanned = scanned.replace(/\s+/g, "").toUpperCase();
    const normalizedExpected = expected.replace(/\s+/g, "").toUpperCase();
    if (normalizedScanned !== normalizedExpected) {
      toast(`AWB mismatch for ${scanTargetBox.box_id}`, "error");
      return { success: false, message: `Mismatch. Scan for order id ${expected_ee_order_id}` };
    }

    setScanning(scanTargetBox.id);
    try {
      const result = await handleScanAWB(expected);
      if (result?.success) {
        setCameraOpen(false);
        setScanTargetBox(null);
      }
      return result;
    } finally {
      setScanning(null);
    }
  }

  // â”€â”€ delete pending box â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleDelete(box) {
    const ids = box?.rowIds || [box?.id].filter(Boolean);
    setDeleting(box?.id);
    try {
      let res;
      if (ids.length > 1) {
        res = await fetch(`${BASE_URL}/vendor/invoices/bulk-delete/`, {
          method: "DELETE",
          headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        });
      } else {
        res = await fetch(`${BASE_URL}/vendor/invoices/${ids[0]}/`, { method: "DELETE", headers: authHeader() });
      }
      if (!res.ok) throw new Error("Delete failed");
      setBoxes(prev => prev.filter(b => b.box_id !== box.box_id));
      toast("Box deleted", "info");
    } catch (e) { toast(e.message, "error"); }
    finally { setDeleting(null); }
  }

  // â”€â”€ refresh boxes from EasyEcom â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleRefresh() {
    if (!groupedBoxes.length) return;
    setRefreshing(true);
    try {
      const ids = boxes.map(b => b.id);
      const res = await fetch(`${BASE_URL}/vendor/invoices/sync/`, {
        method: "POST",
        headers: { ...authHeader(), "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Refresh failed");

      const records = data?.data?.records || [];
      if (records.length) {
        const byId = Object.fromEntries(records.map(r => [r.id, r]));
        setBoxes(prev => prev.map(b => byId[b.id] ? { ...b, ...byId[b.id] } : b));
      }
      const synced = data?.data?.synced ?? 0;
      const failed = data?.data?.failed ?? 0;
      if (failed > 0 && synced === 0) {
        toast(`Refresh failed for all ${failed} box(es)`, "error");
      } else if (failed > 0) {
        toast(`${synced} synced - ${failed} had no EE data`, "info");
      } else {
        toast(`${synced} box${synced !== 1 ? "es" : ""} refreshed from EasyEcom`, "success");
      }
    } catch (e) { toast("Refresh error: " + e.message, "error"); }
    finally { setRefreshing(false); }
  }

  // â”€â”€ update vendor status â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  async function handleStatusChange(newGroup) {
    const mapped = newGroup === "completed" ? "fully_dispatched" : "open";
    setUpdatingStatus(true);
    try {
      for (const req of requirementGroup) {
        await fetch(`${BASE_URL}/vendor/requirements/${req.id}/`, {
          method: "PUT",
          headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify({ vendor_status: mapped }),
        });
      }
      setVendorStatusGroup(newGroup);
      toast(`Status set to ${newGroup === "completed" ? "Completed" : "Open"}`, "success");
      if (newGroup === "completed") {
        setTimeout(() => navigate("/purchase/invoicing"), 1200);
      }
    } catch (e) { toast(e.message, "error"); }
    finally { setUpdatingStatus(false); }
  }

  function handleAction(type, box) {
    const url = type === "invoice" ? box.ee_invoice_url : box.ee_label_url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      toast(`${type === "invoice" ? "Invoice" : "Label"} not yet available for ${box.box_id}`, "info");
    }
  }

  // ── download all labels (confirmed + shipped boxes) ────────────────────────
  async function handleDownloadLabels() {
    const labelBoxes = boxes.filter(b => b.tracking_status === "booked");
    if (!labelBoxes.length) {
      toast("No confirmed boxes with labels to download", "info");
      return;
    }
    const ids = labelBoxes.map(b => b.id);
    setDownloadingLabels(true);
    try {
      const res = await fetch(
        `${BASE_URL}/vendor/invoices/labels/download/?ids=${encodeURIComponent(JSON.stringify(ids))}`,
        { headers: authHeader() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message || "Download failed");
      }
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `labels_${firstReq.request_code || "download"}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);
      toast(`${labelBoxes.length} label${labelBoxes.length !== 1 ? "s" : ""} downloaded`, "success");
    } catch (e) {
      toast("Download error: " + e.message, "error");
    } finally {
      setDownloadingLabels(false);
    }
  }

  // â”€â”€ derived stats â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const totalRequired  = requirementGroup.reduce((s, r) => s + (Number(r.required_qty) || 0), 0);
  const totalPacked    = boxes.reduce((s, b) => s + (Number(b.qty) || 0), 0);

  // â”€â”€ render â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* â”€â”€ sticky page header â€” sits below Navbar (56 px = top-14) â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="sticky top-14 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-[1500px] mx-auto px-3 sm:px-5 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => navigate("/purchase/invoicing")}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-800 transition-colors shrink-0"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                <span className="font-bold text-gray-900 text-sm sm:text-base truncate">
                  <span className="font-mono">{firstReq.request_code}</span>
                </span>
                <span className="hidden sm:inline text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">Processing</span>
              </div>
              <p className="text-xs text-gray-400 hidden sm:block">
                Vendor: {firstReq.vendor_name || "â€”"} - {requirementGroup.length} SKU line{requirementGroup.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Refresh â€” syncs all visible boxes from EE */}
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-2.5 sm:px-4 gap-1.5 text-xs font-medium"
              onClick={handleRefresh}
              disabled={refreshing || !groupedBoxes.length}
              title="Refresh from EasyEcom"
            >
              <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>

            {/* Scanner */}
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-2.5 sm:px-4 gap-1.5 text-xs font-medium"
              onClick={() => { setScanTargetBox(null); setCameraOpen(true); }}
              title="Open Scanner"
            >
              <QrCode size={14} />
              <span className="hidden sm:inline">Scanner</span>
            </Button>

            {/* Download Labels */}
            <Button
              size="sm"
              variant="outline"
              className="h-9 px-2.5 sm:px-4 gap-1.5 text-xs font-medium"
              onClick={handleDownloadLabels}
              disabled={downloadingLabels || !boxes.some(b => ["booked", "in_transit", "out_for_delivery", "delivered"].includes(b.tracking_status))}
              title="Download all labels"
            >
              {downloadingLabels ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
              <span className="hidden sm:inline">Labels</span>
            </Button>

            {/* Vendor Status */}
            <div className="relative">
              <select
                value={vendorStatusGroup}
                onChange={e => handleStatusChange(e.target.value)}
                disabled={updatingStatus}
                className={cn(
                  "h-9 pl-3 pr-8 rounded-lg border text-xs font-medium focus:outline-none focus:ring-2 focus:ring-violet-400 appearance-none cursor-pointer",
                  vendorStatusGroup === "completed"
                    ? "bg-green-50 border-green-300 text-green-800"
                    : "bg-white border-gray-200 text-gray-700"
                )}
              >
                <option value="open">Open</option>
                <option value="completed">Completed</option>
              </select>
              {updatingStatus
                ? <RefreshCw size={12} className="animate-spin absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
                : <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-5 py-5">

        {/* â”€â”€ progress bar â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="mb-5 bg-white rounded-xl border border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="font-semibold text-gray-700">Shipment Progress</span>
            <span className="text-xs text-gray-400">{shipped.length} / {groupedBoxes.length} boxes shipped - {totalPacked} / {totalRequired} units packed</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-2 rounded-full bg-gradient-to-r from-violet-500 to-violet-400 transition-all duration-500"
              style={{ width: groupedBoxes.length ? `${(shipped.length / groupedBoxes.length) * 100}%` : "0%" }}
            />
          </div>
          <div className="flex gap-4 mt-3 text-xs text-gray-500">
            <span><span className="font-bold text-orange-600">{pending.length}</span> Pending</span>
            <span><span className="font-bold text-blue-600">{confirmed.length}</span> Confirmed</span>
            <span><span className="font-bold text-green-600">{shipped.length}</span> Shipped</span>
            <span><span className="font-bold text-gray-800">{groupedBoxes.length}</span> Total boxes</span>
          </div>
        </div>

        {/* â”€â”€ 2-column layout â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">

          {/* â•â•â•â• LEFT COLUMN â•â•â•â• */}
          <div className="flex flex-col gap-5 min-w-0">

            {/* SKUs in request */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
                <span className="font-semibold text-sm text-gray-800">SKUs in request</span>
                <span className={cn(
                  "text-xs font-semibold px-2.5 py-1 rounded-full",
                  totalPacked >= totalRequired ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                )}>
                  {Math.max(0, totalRequired - totalPacked)} units remaining
                </span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/60 text-xs text-gray-500 font-semibold uppercase tracking-wide">
                    <th className="text-left px-5 py-2.5">SKU</th>
                    <th className="text-left px-4 py-2.5">Name</th>
                    <th className="text-right px-5 py-2.5">Remaining qty</th>
                  </tr>
                </thead>
                <tbody>
                  {requirementGroup.map(req => {
                    const dispatched  = dispatchedByReqId[String(req.id)] || 0;
                    const remaining   = Math.max(0, (Number(req.required_qty) || 0) - dispatched);
                    const isDone      = remaining === 0;
                    return (
                      <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3 font-mono text-xs font-bold text-violet-700">{req.sku}</td>
                        <td className="px-4 py-3 text-gray-700">{req.sku_name || "â€”"}</td>
                        <td className="px-5 py-3 text-right">
                          <span className={cn(
                            "inline-block min-w-[2rem] text-center font-bold text-base px-2 py-0.5 rounded-lg",
                            isDone   ? "bg-gray-900 text-white" :
                            remaining > 10 ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                          )}>
                            {remaining}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Quick add boxes */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-800 m-0">Quick add boxes</p>
                <p className="text-xs text-gray-400 mt-0.5">Split a single SKU across multiple identical boxes.</p>
              </div>

              <div className="p-5 flex flex-col gap-4">

                {/* Row 1 â€” Invoice (full width, big & prominent) */}
                <div>
                  <label className="text-[11px] text-gray-900 block mb-1.5">Invoice no.</label>
                  <Input
                    value={qaInvoice}
                    onChange={e => setQaInvoice(e.target.value)}
                    placeholder="INV-001"
                    className="h-14 text-xl font-semibold font-mono text-center tracking-widest border-[1.5px] border-gray-300 focus:border-blue-400 bg-gray-50"
                  />
                </div>

                {/* Row 2 â€” No. of boxes + Weight */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-1.5">No. of boxes</label>
                    <Input
                      type="number" min="1"
                      value={qaNumBoxes}
                      onChange={e => setQaNumBoxes(e.target.value)}
                      className="h-11 text-lg font-semibold font-mono text-center bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-1.5">Weight (kg)</label>
                    <Input
                      type="number" min="0" step="1"
                      value={qaWeight}
                      onChange={e => setQaWeight(sanitizeIntegerInput(e.target.value))}
                      placeholder="1"
                      className="h-11 text-lg font-semibold font-mono text-center bg-gray-50"
                    />
                  </div>
                </div>

                {/* Row 3 â€” SKU + Qty per box */}
                <div className="grid grid-cols-[1fr_100px] gap-3 items-end">
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-1.5">SKU</label>
                    <select
                      value={qaReqId}
                      onChange={e => setQaReqId(e.target.value)}
                      className="w-full h-11 border border-gray-200 rounded-lg px-3 text-sm font-medium text-gray-800 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    >
                      {requirementGroup.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.sku} {r.sku_name ? `- ${r.sku_name}` : ""} - {Math.max(0, (Number(r.required_qty) || 0) - (dispatchedByReqId[String(r.id)] || 0))} left
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500 block mb-1.5">Qty / box</label>
                    <Input
                      type="number" min="1"
                      value={qaQtyPerBox}
                      onChange={e => setQaQtyPerBox(e.target.value)}
                      className="h-11 text-lg font-semibold font-mono text-center bg-gray-50"
                    />
                  </div>
                </div>

                {/* Info line */}
                {qaReqId && qaNumBoxes && qaQtyPerBox && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-500 m-0">
                      Will use <span className="font-semibold font-mono text-gray-800">{parseInt(qaNumBoxes, 10) * parseInt(qaQtyPerBox, 10) || 0}</span> units
                      {" - "}remaining for {reqMap[qaReqId]?.sku}:{" "}
                      <span className="font-semibold font-mono text-gray-800">
                        {Math.max(0, (Number(reqMap[qaReqId]?.required_qty) || 0) - (dispatchedByReqId[String(qaReqId)] || 0))}
                      </span>
                    </p>
                  </div>
                )}

                {/* Add button */}
                <Button
                  className="w-full h-11 bg-gray-900 hover:bg-gray-700 text-white font-medium gap-2"
                  onClick={handleQuickAdd}
                  disabled={qaAdding}
                >
                  {qaAdding ? <RefreshCw size={14} className="animate-spin" /> : <BoxIcon size={14} />}
                  Add boxes
                </Button>
              </div>

              {/* Footer â€” Create manually */}
              <div className="border-t border-gray-100 px-5 py-3">
                <button
                  onClick={() => setShowManual(s => !s)}
                  className="w-full h-10 text-sm text-gray-500 hover:text-gray-800 hover:bg-gray-50 border border-dashed border-gray-300 hover:border-gray-400 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus size={14} /> Create box manually
                </button>
              </div>
            </div>

            {/* Manual box builder */}
            {showManual && (
              <div className="bg-white rounded-xl border border-violet-200 shadow-sm p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-sm text-gray-800">Manual Box Builder</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Create a single box with mixed SKUs.</p>
                  </div>
                  <button onClick={() => setShowManual(false)} className="text-gray-400 hover:text-gray-700">
                    <X size={16} />
                  </button>
                </div>

                {/* Lines */}
                <div className="flex flex-col gap-2 mb-4">
                  {manLines.map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <select
                        value={line.reqId}
                        onChange={e => setManLines(p => p.map((l, i) => i === idx ? { ...l, reqId: e.target.value } : l))}
                        className="flex-1 h-9 border border-gray-200 rounded-lg px-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-violet-400 bg-white"
                      >
                        <option value="">Select SKUâ€¦</option>
                        {requirementGroup.map(r => (
                          <option key={r.id} value={r.id}>{r.sku} - {r.sku_name || ""}</option>
                        ))}
                      </select>
                      <Input
                        type="number" min="1"
                        value={line.qty}
                        onChange={e => setManLines(p => p.map((l, i) => i === idx ? { ...l, qty: e.target.value } : l))}
                        placeholder="Qty"
                        className="w-20 h-9 text-sm"
                      />
                      {manLines.length > 1 && (
                        <button
                          onClick={() => setManLines(p => p.filter((_, i) => i !== idx))}
                          className="text-gray-300 hover:text-red-400"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setManLines(p => [...p, { reqId: defaultReqId, qty: "1" }])}
                    className="text-xs text-violet-600 hover:text-violet-800 font-medium flex items-center gap-1 self-start"
                  >
                    <Plus size={12} /> Add another SKU
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Invoice No.</label>
                    <Input value={manInvoice} onChange={e => setManInvoice(e.target.value)} placeholder="INV-001" className="h-9 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 block mb-1">Weight (kg)</label>
                    <Input
                      type="number" min="0" step="1"
                      value={manWeight}
                      onChange={e => setManWeight(sanitizeIntegerInput(e.target.value))}
                      placeholder="1"
                      className="h-9 text-sm"
                    />
                  </div>
                </div>

                <Button
                  size="sm"
                  className="h-9 bg-gray-900 hover:bg-gray-700 text-white gap-1.5"
                  onClick={handleManualAdd}
                  disabled={manAdding}
                >
                  {manAdding ? <RefreshCw size={13} className="animate-spin" /> : <Package size={13} />}
                  Create Box
                </Button>
              </div>
            )}
          </div>

          {/* â•â•â•â• RIGHT COLUMN â•â•â•â• */}
          <div className="flex flex-col gap-4">

            {/* Camera scanner modal */}
            {cameraOpen && (
              <CameraScanner
                onScanned={handleCameraScanned}
                onClose={() => { setCameraOpen(false); setScanTargetBox(null); }}
                expectedAwb={scanTargetBox?.ee_awb_no || ""}
                expectedBoxId={scanTargetBox?.box_id || ""}
              />
            )}

            {/* â”€â”€ Pending â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <SectionHeader
                    icon={Package}
                    title="Pending"
                    count={pending.length}
                    color="bg-orange-50 border-orange-200 text-orange-800"
                    collapsed={!pendingOpen}
                    onToggle={() => setPendingOpen(s => !s)}
                  />
                </div>
                {pending.length > 0 && (
                  <Button
                    size="sm"
                    className="h-10 bg-gray-900 hover:bg-gray-700 text-white gap-1.5 text-xs shrink-0"
                    onClick={handleConfirmAll}
                    disabled={confirmingAll}
                  >
                    {confirmingAll
                      ? <RefreshCw size={13} className="animate-spin" />
                      : <CheckCircle2 size={13} />}
                    Confirm All ({pending.length})
                  </Button>
                )}
              </div>
              {pendingOpen && (
                <div className="flex flex-col gap-3 mt-3">
                  {loadingBoxes ? (
                    <div className="text-center py-6 text-sm text-gray-400">Loadingâ€¦</div>
                  ) : pending.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                      No pending boxes. Use "Quick add boxes" to create some.
                    </div>
                  ) : pending.map(box => (
                    <BoxCard
                      key={box.box_id || box.id}
                      box={box}
                      reqMap={reqMap}
                      onConfirm={handleConfirm}
                      onDelete={handleDelete}
                      onAction={handleAction}
                      confirming={confirming}
                      deleting={deleting}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* â”€â”€ Confirmed â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div>
              <SectionHeader
                icon={CheckCircle2}
                title="Confirmed"
                count={confirmed.length}
                color="bg-blue-50 border-blue-200 text-blue-800"
                collapsed={!confirmedOpen}
                onToggle={() => setConfirmedOpen(s => !s)}
              />
              {confirmedOpen && (
                <div className="flex flex-col gap-3 mt-3">
                  {confirmed.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                      No confirmed boxes yet.
                    </div>
                  ) : confirmed.map(box => (
                    <BoxCard
                      key={box.box_id || box.id}
                      box={box}
                      reqMap={reqMap}
                      onScan={handleScan}
                      onAction={handleAction}
                      scanning={scanning}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* â”€â”€ Scanned & Shipped â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div>
              <SectionHeader
                icon={Truck}
                title="Scanned & Shipped"
                count={shipped.length}
                color="bg-green-50 border-green-200 text-green-800"
                collapsed={!shippedOpen}
                onToggle={() => setShippedOpen(s => !s)}
              />
              {shippedOpen && (
                <div className="flex flex-col gap-3 mt-3">
                  {shipped.length === 0 ? (
                    <div className="text-center py-6 text-sm text-gray-400 bg-white rounded-xl border border-dashed border-gray-200">
                      No shipped boxes yet.
                    </div>
                  ) : shipped.map(box => (
                    <BoxCard
                      key={box.box_id || box.id}
                      box={box}
                      reqMap={reqMap}
                      onAction={handleAction}
                    />
                  ))}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      <Toast toasts={toasts} remove={removeToast} />
    </div>
  );
}
